import { getMomentTz, MESApiService, spParameter } from "@dexteel/mesf-core";
import { get } from "lodash-es";
import { Part, PartLog, PartsResult, PassesCampaignData } from "../models/Part";
import { PartCampaignData } from "../models/PartCampaingData";

const moment = getMomentTz();

export const getParts = async (
	params: { partId?: number },
	signal?: AbortSignal,
) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [];

	if (params.partId) parameters.push({ name: "PartId", value: params.partId });

	const resp = await apiService.callV2("[M2].[GetParts]", parameters, signal);

	if (resp.ok) {
		let parts = get(resp, "data.tables[0].rows", []) as Part[];
		parts = parts.map((p) => ({
			...p,
			InstalledAt: p.InstalledAt ? moment.utc(p["InstalledAt"]).toDate() : null,
			UninstalledAt: p.UninstalledAt
				? moment.utc(p["UninstalledAt"]).toDate()
				: null,
			LastResetTimestamp: p.LastResetTimestamp
				? moment.utc(p["LastResetTimestamp"]).toDate()
				: null,
		}));

		const passes = get(resp, "data.tables[1].rows", []) as PassesCampaignData[];

		return { parts, passes } as PartsResult;
	} else {
		throw new Error(resp.message || "Error fetching parts");
	}
};

export const getPartLogs = async (
	params: { partId: number },
	signal?: AbortSignal,
) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [{ name: "PartId", value: params.partId }];

	const resp = await apiService.callV2(
		"[M2].[GetPartLogs]",
		parameters,
		signal,
	);

	if (resp.ok) {
		let rows = get(resp, "data.tables[0].rows", []);

		rows = rows.map((pL: PartLog) => ({
			...pL,
			Timestamp: pL.Timestamp ? moment.utc(pL["Timestamp"]).toDate() : null,
		}));

		return rows as PartLog[];
	} else {
		throw new Error(resp.message || "Error fetching part logs");
	}
};

export const getPartCampaignData = async (
	params: { partId: number; start: Date | undefined; end: Date | undefined },
	signal?: AbortSignal,
) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [{ name: "PartId", value: params.partId }];

	if (params.start) {
		parameters.push({ name: "Start", value: params.start.toISOString() });
	}
	if (params.end) {
		parameters.push({ name: "End", value: params.end.toISOString() });
	}

	const resp = await apiService.callV2(
		"[M2].[GetPartCampaignData]",
		parameters,
		signal,
	);

	if (resp.ok) {
		let rows = get(resp, "data.tables[0].rows", []);

		rows = rows.map((pL: PartCampaignData) => ({
			...pL,
			Timestamp: pL.Timestamp ? moment.utc(pL["Timestamp"]).toDate() : null,
		}));

		return rows as PartCampaignData[];
	} else {
		throw new Error(resp.message || "Error fetching part campaign data");
	}
};

type updatePartCampaignOffsetProps = {
	partCampaignId: number | null;
	offsetBillets?: number;
	offsetLength?: number;
	offsetWeight: number;
};

export const updatePartCampaignOffset = async ({
	partCampaignId,
	offsetBillets,
	offsetLength,
	offsetWeight,
}: updatePartCampaignOffsetProps) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{ name: "PartCampaignId", value: partCampaignId },
	];

	if (offsetBillets)
		parameters.push({ name: "OffsetBillets", value: offsetBillets });
	if (offsetLength)
		parameters.push({ name: "OffsetLength", value: offsetLength });
	if (offsetWeight)
		parameters.push({ name: "OffsetWeight", value: offsetWeight });

	const resp = await apiService.callV2(
		"[M2].[UpdatePartCampaignOffsets]",
		parameters,
	);

	if (!resp.ok) {
		throw new Error(resp.message || "Error updating part campaign offsets");
	}
};

type upsertPartResetProps = {
	partResetId: number | null;
	partId: number;
	resetAt: Date;
	resetBy: number;
	comments: string | null;
};

export const upsertPartReset = async ({
	partResetId,
	partId,
	resetAt,
	resetBy,
	comments,
}: upsertPartResetProps) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{ name: "PartCampaignId", value: partResetId },
		{ name: "PartId", value: partId },
	];

	if (resetAt)
		parameters.push({ name: "ResetAt", value: resetAt.toISOString() });
	if (resetBy) parameters.push({ name: "ResetBy", value: resetBy });
	if (comments) parameters.push({ name: "Comments", value: comments });

	const resp = await apiService.callV2("[M2].[UpsertPartReset]", parameters);

	if (!resp.ok) {
		throw new Error(resp.message || "Error updating part reset");
	}
};
